import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowRight, Sparkles } from "lucide-react";

const TOOL_SLUGS = ["abundance-affirmation-deck", "self-discovery-journal"];

export default function SacredTools() {
  usePageMeta(
    "Sacred Tools — Just be you by Mehjabin",
    "Physical companions for the inner work — the Abundance Affirmation Deck and the Self-Discovery Journal, hand-curated by Mehjabin."
  );
  const [tools, setTools] = useState([]);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/products").then((r) => {
      const bySlug = Object.fromEntries(r.data.map((p) => [p.slug, p]));
      setTools(TOOL_SLUGS.map((s) => bySlug[s]).filter(Boolean));
    });
  }, []);

  return (
    <div data-testid="sacred-tools-page">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-14 overflow-hidden">
        <div className="absolute -top-20 left-1/3 w-[460px] h-[460px] bg-peach/25 organic-blob blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="overline mb-6 inline-flex items-center gap-2 justify-center">
            <Sparkles size={12} /> Held in the hands, felt in the body
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
            Sacred <span className="italic text-lapis">Tools</span>
          </h1>
          <p className="mt-7 max-w-xl mx-auto text-ink/65 leading-relaxed">
            Two physical companions for the inner work — objects to hold, return to, and build ritual around.
            Hand-curated, made to last a becoming.
          </p>
        </div>
      </section>

      {/* TOOLS */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24 space-y-16">
        {tools.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8 }}
            className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}
            data-testid={`sacred-tool-${p.slug}`}
          >
            <div className="md:[direction:ltr]">
              <Link to={`/shop/${p.slug}`} className="block group">
                <div className="relative overflow-hidden rounded-[2.5rem] aspect-[4/5] shadow-xl shadow-lapis/10">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                  />
                  <div className="absolute top-5 left-5 overline bg-canvas/85 backdrop-blur px-3.5 py-1.5 rounded-full">
                    Physical · ships to you
                  </div>
                </div>
              </Link>
            </div>
            <div className="md:[direction:ltr]">
              <h2 className="font-serif font-light text-3xl md:text-4xl text-ink leading-tight">{p.title}</h2>
              <p className="mt-5 text-ink/65 leading-relaxed">{p.long_description || p.description}</p>
              <div className="mt-7 font-serif text-2xl text-lapis">{format(p.price, p.price_inr)}</div>
              <Link
                to={`/shop/${p.slug}`}
                className="btn-primary mt-7 inline-flex"
                data-testid={`sacred-tool-view-${p.slug}`}
              >
                View & order <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        ))}
      </section>

      {/* QUIET SHOP LINK */}
      <section className="pb-24 text-center" data-testid="sacred-tools-shop-link">
        <div className="divider-soft max-w-xs mx-auto mb-10" />
        <p className="text-sm text-ink/55">
          Looking for the e-books, journals & meditations?{" "}
          <Link to="/shop" className="text-lapis font-semibold border-b border-lapis/40 hover:border-lapis pb-0.5">
            Visit the full shop →
          </Link>
        </p>
      </section>
    </div>
  );
}
